/**
 * Card component displaying a single AI Block execution with metadata, results, and field changes.
 */

import _ from 'lodash'
import React, { useState, useMemo } from 'react'
import { Icon } from '@blueprintjs/core'
import { IconNames } from '@blueprintjs/icons'
import { StatusBadge } from 'browser/components/atomic-elements/atoms/status-badge/status-badge'
import { getStatusConfig, formatRunTimestamp, computeFieldRows } from './ai-block-activity-helpers'
import type { IAiBlockActivityOptions, ExtendedBlockEvent } from './ai-block-activity-types'
import { TokenUsageInfo } from './token-usage-info'
import { OverallConfidenceDisplay } from './overall-confidence-display'
import { FieldsTable } from './fields-table'

interface IAiBlockExecutionCardProps {
  options: IAiBlockActivityOptions
  execution: ExtendedBlockEvent
  runNumber?: number
}

export const AiBlockExecutionCard: React.FC<IAiBlockExecutionCardProps> = ({ options, execution, runNumber }) => {
  const { aiBlock, timing, status, creationDate, result, runIndex, operation, tokenUsage, errorMsg } = execution
  const { label: statusLabel, color: statusColor } = getStatusConfig(status)

  const blockName = aiBlock?.displayName || aiBlock?.entityId?.substring(0, 8) || 'Unknown'
  const version = (aiBlock as any)?.version
  const timestamp = formatRunTimestamp(timing?.receivedDate || creationDate)
  const displayRunIndex = runIndex ?? runNumber
  const action = result?.action || 'unknown'
  const hasError = status === 'failed' || errorMsg != null

  // Check if there are any changes
  const fieldRows = useMemo(() => {
    return computeFieldRows(options, result?.changeSet, result?.extractedFields)
  }, [options, result?.changeSet, result?.extractedFields])
  
  const hasChanges = fieldRows.length > 0
  
  // Default to collapsed if no changes
  const [isExpanded, setIsExpanded] = useState(hasChanges)
  
  const toggleExpanded = () => {
    setIsExpanded(!isExpanded)
  }

  return (
    <div className='c-aiBlockResults-runCard'>
      {/* Header */}
      <div
        className='c-aiBlockResults-runCard-header'
        role='button'
        tabIndex={0}
        onClick={toggleExpanded}
        onKeyDown={(e) => { if (e.key === 'Enter' || e.key === ' ') { e.preventDefault(); toggleExpanded() } }}
        style={{ cursor: 'pointer' }}
      >
        <div className='c-aiBlockResults-runCard-headerLeft'>
          <span className='c-aiBlockResults-blockName'>{blockName}</span>
          {version && (
            <span className='c-aiBlockResults-versionTag'>{version}</span>
          )}
          {displayRunIndex != null && (
            <span className='c-aiBlockResults-runIndex'>Run #{displayRunIndex}</span>
          )}
          {operation && (
            <span className='c-aiBlockResults-operationBadge'>{_.capitalize(operation)}</span>
          )}
          <span className='c-aiBlockResults-actionBadge' title='Action result'>
            {_.capitalize(action)}
          </span>
          {!hasChanges && (
            <span className='c-aiBlockResults-noChangesLabel'>No changes</span>
          )}
        </div>
        <div className='c-aiBlockResults-runCard-headerRight'>
          <span className='c-aiBlockResults-timestamp'>{timestamp}</span>
          <StatusBadge color={statusColor}>{statusLabel}</StatusBadge>
          <Icon
            icon={isExpanded ? IconNames.CHEVRON_UP : IconNames.CHEVRON_DOWN}
            iconSize={16}
            className='c-aiBlockResults-expandChevron'
          />
        </div>
      </div>

      {/* Show details only when expanded */}
      {isExpanded && (
        <>
          {/* Meta info */}
          <TokenUsageInfo tokenUsage={tokenUsage} duration={timing?.durationMs} />

          {/* Overall confidence and reasoning (if present) */}
          <OverallConfidenceDisplay confidence={result?.confidence} reasoning={result?.reasoning} />

          {/* TODO: Error message (if failed). Display for debugging only; should be hidden from users to avoid excessive verbosity */}
          {hasError && (
            <div className='c-aiBlockResults-runCard-error'>
              <div className='c-aiBlockResults-errorLabel'>Error:</div>
              <div className='c-aiBlockResults-errorMessage'>{errorMsg}</div>
            </div>
          )}

          {/* Combined fields table showing changes and confidence */}
          {!hasError && (
            <div className='c-aiBlockResults-runCard-body'>
              <FieldsTable
                options={options}
                changeSet={result?.changeSet}
                extractedFields={result?.extractedFields}
              />
            </div>
          )}
        </>
      )}
    </div>
  )
}
